import type { StackConfig } from '@shared/types';

export type StackParseResult = {
  type: 'stack';
  value: string[];
  stackConfig: StackConfig;
};

/**
 * Detects stack syntax: `// stack: [min:max] [opt1, opt2, ...]`
 *
 * The comment must start with "stack:" followed by an optional min:max
 * bounds pair and an option list in square brackets.
 * The default value must be a 1D string array literal.
 *
 * Examples:
 *   `layout = ["pot","jack"]; // stack: 1:10 [pot, jack, empty]`
 *   `layout = ["pot"];        // stack: [pot, jack, empty]`
 *
 * Returns null if the comment does not match the stack pattern.
 */
export function parseStackType(
  valueStr: string,
  comment: string,
): StackParseResult | null {
  const stackMatch = comment.match(
    /^stack:\s*(?:(\d+):(\d+)\s*)?\[([^\]]+)\]/i,
  );
  if (!stackMatch) return null;

  const minRaw = stackMatch[1];
  const maxRaw = stackMatch[2];
  const min = minRaw !== undefined ? parseInt(minRaw, 10) : undefined;
  const max = maxRaw !== undefined ? parseInt(maxRaw, 10) : undefined;

  // Parse option tokens — each may carry optional #hex colour suffix,
  // e.g. "pot#ff3300" → name "pot", colour "#ff3300".
  const rawTokens = stackMatch[3]
    .split(',')
    .map((s) => s.trim().replace(/^["']|["']$/g, ''))
    .filter((s) => s.length > 0);
  const rawOpts: string[] = [];
  const optionColours: Record<string, string> = {};

  for (const token of rawTokens) {
    const hashIdx = token.indexOf('#');
    let name: string;
    if (
      hashIdx > 0 &&
      /^#[0-9a-fA-F]{3,8}$/.test(token.slice(hashIdx))
    ) {
      name = token.slice(0, hashIdx);
      optionColours[name] = token.slice(hashIdx);
    } else {
      name = token;
    }
    rawOpts.push(name);
  }

  if (rawOpts.length === 0) return null;

  const hasCustomColours = Object.keys(optionColours).length > 0;

  // Parse the default 1D array literal ["a", "b", "c"] into string[].
  const flatDefault: string[] = [];
  const innerMatch = valueStr.match(/^\[\s*(.*?)\s*\]$/s);
  if (innerMatch) {
    const cells = innerMatch[1]
      .split(',')
      .map((s) => s.trim().replace(/^["']|["']$/g, ''))
      .filter((s) => s.length > 0);
    flatDefault.push(...cells);
  }

  // Validate: replace unknown values with the first option.
  const validated = flatDefault.map((cell) =>
    rawOpts.includes(cell) ? cell : rawOpts[0],
  );

  // Pad to min if the list is shorter than the minimum.
  if (min !== undefined) {
    while (validated.length < min) validated.push(rawOpts[0]);
  }
  // Ensure at least one item when the list is empty.
  if (validated.length === 0) validated.push(rawOpts[0]);

  // Truncate to max if the list is longer than the maximum.
  if (max !== undefined && validated.length > max) {
    validated.length = max;
  }

  return {
    type: 'stack',
    value: validated,
    stackConfig: {
      ...(min !== undefined ? { min } : {}),
      ...(max !== undefined ? { max } : {}),
      options: rawOpts,
      ...(hasCustomColours ? { optionColours } : {}),
    },
  };
}
